import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { type Theme, applyTheme, loadTheme, resolveTheme, saveTheme } from './theme.js';

interface ThemeContextValue {
  theme: Theme;
  resolved: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  /** 라이트 ↔ 다크 즉시 토글 (현재 resolved 기준). */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => loadTheme());
  const resolved = resolveTheme(theme);

  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  // 시스템 컬러스킴 변경 추종 (theme === 'system' 일 때만).
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (theme === 'system') applyTheme(resolveTheme('system'));
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  // ⌘⇧L / Ctrl+Shift+L 로 라이트/다크 즉시 토글. 입력 중에도 동작.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        !e.altKey &&
        (e.code === 'KeyL' || e.key === 'l' || e.key === 'L')
      ) {
        e.preventDefault();
        const next: Theme = resolved === 'dark' ? 'light' : 'dark';
        setThemeState(next);
        saveTheme(next);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [resolved]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      resolved,
      setTheme: (next) => {
        setThemeState(next);
        saveTheme(next);
      },
      toggle: () => {
        const next: Theme = resolved === 'dark' ? 'light' : 'dark';
        setThemeState(next);
        saveTheme(next);
      },
    }),
    [theme, resolved],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
